import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';

const TradingChart = ({ symbol, timeframe, chartType, indicators, selectedTool }) => {
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);

  // Mock real-time price data generation
  useEffect(() => {
    const generateMockData = () => {
      const data = [];
      const basePrice = 1.0850;
      let currentPrice = basePrice;
      const now = new Date();

      for (let i = 100; i >= 0; i--) {
        const timestamp = new Date(now.getTime() - i * getTimeframeMs(timeframe));
        const change = (Math.random() - 0.5) * 0.002;
        currentPrice += change;
        
        const open = currentPrice;
        const high = open + Math.random() * 0.001;
        const low = open - Math.random() * 0.001;
        const close = low + Math.random() * (high - low);
        const volume = Math.floor(Math.random() * 1000000) + 500000;

        data?.push({
          timestamp: timestamp?.getTime(),
          time: timestamp?.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          }),
          open: parseFloat(open?.toFixed(5)),
          high: parseFloat(high?.toFixed(5)),
          low: parseFloat(low?.toFixed(5)),
          close: parseFloat(close?.toFixed(5)),
          volume,
          sma: parseFloat((close + (Math.random() - 0.5) * 0.0005)?.toFixed(5)),
          ema: parseFloat((close + (Math.random() - 0.5) * 0.0003)?.toFixed(5)),
          rsi: Math.floor(Math.random() * 40) + 30,
          macd: (Math.random() - 0.5) * 0.0002
        });
      }

      return data;
    };

    setIsLoading(true);
    const data = generateMockData();
    setChartData(data);
    
    if (data?.length > 0) {
      const latest = data?.[data?.length - 1];
      const previous = data?.[data?.length - 2];
      setCurrentPrice(latest?.close);
      setPriceChange(latest?.close - previous?.close);
    }
    
    setIsLoading(false);
  }, [symbol, timeframe]);

  // Real-time price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setChartData(prevData => {
        if (prevData?.length === 0) return prevData;
        
        const newData = [...prevData];
        const lastPoint = newData?.[newData?.length - 1];
        const change = (Math.random() - 0.5) * 0.0005;
        const newPrice = lastPoint?.close + change;
        
        // Update the last data point for real-time effect
        newData[newData.length - 1] = {
          ...lastPoint,
          close: parseFloat(newPrice?.toFixed(5)),
          high: Math.max(lastPoint?.high, newPrice),
          low: Math.min(lastPoint?.low, newPrice)
        };

        setCurrentPrice(newPrice);
        setPriceChange(change);
        
        return newData;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getTimeframeMs = (tf) => {
    const timeframes = {
      '1m': 60000,
      '5m': 300000,
      '15m': 900000,
      '1h': 3600000,
      '4h': 14400000,
      '1d': 86400000
    };
    return timeframes?.[tf] || 60000;
  };

  const formatPrice = (price) => {
    return price?.toFixed(5) || '0.00000';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-md p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Open:</span>
              <span className="font-mono">{formatPrice(data?.open)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">High:</span>
              <span className="font-mono text-green-600">{formatPrice(data?.high)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Low:</span>
              <span className="font-mono text-red-600">{formatPrice(data?.low)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Close:</span>
              <span className="font-mono">{formatPrice(data?.close)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Volume:</span>
              <span className="font-mono">{data?.volume?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>Loading chart data...</span>
          </div>
        </div>
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 }
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              domain={['dataMin - 0.001', 'dataMax + 0.001']}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={formatPrice}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: "var(--color-primary)" }}
            />
            {indicators?.includes('sma') && (
              <Line 
                type="monotone" 
                dataKey="sma" 
                stroke="var(--color-warning)" 
                strokeWidth={1}
                dot={false}
                strokeDasharray="5 5"
              />
            )}
            {indicators?.includes('ema') && (
              <Line 
                type="monotone" 
                dataKey="ema" 
                stroke="var(--color-success)" 
                strokeWidth={1}
                dot={false}
                strokeDasharray="3 3"
              />
            )}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              domain={['dataMin - 0.001', 'dataMax + 0.001']}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={formatPrice}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="close" 
              stroke="var(--color-primary)" 
              fill="var(--color-primary)"
              fillOpacity={0.1}
              strokeWidth={2}
            />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <Tooltip />
            <Bar dataKey="volume" fill="var(--color-muted)" />
          </BarChart>
        );

      default: // candlestick
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              domain={['dataMin - 0.001', 'dataMax + 0.001']}
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={formatPrice}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="high" 
              stroke="var(--color-success)" 
              strokeWidth={1}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="low" 
              stroke="var(--color-error)" 
              strokeWidth={1}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="w-full h-full bg-card flex flex-col">
      {/* Price Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">{symbol}</h2>
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-mono font-bold text-foreground">
              {formatPrice(currentPrice)}
            </span>
            <span className={`text-sm font-medium ${
              priceChange >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {priceChange >= 0 ? '+' : ''}{formatPrice(priceChange)} 
              ({priceChange >= 0 ? '+' : ''}{((priceChange / currentPrice) * 100)?.toFixed(2)}%)
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Live</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date()?.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit',
              hour12: false 
            })}
          </span>
        </div>
      </div>
      {/* Chart Area */}
      <div className="flex-1 p-4">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {/* Chart Controls */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Active Tool:</span>
            <span className="text-sm font-medium text-foreground capitalize">
              {selectedTool || 'Cursor'}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {indicators?.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Indicators:</span>
                <div className="flex space-x-1">
                  {indicators?.map((indicator) => (
                    <span 
                      key={indicator}
                      className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md uppercase"
                    >
                      {indicator}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingChart;